import { combineLatest, Observable } from "rxjs";
import { filter, map, take } from "rxjs/operators";
import { AfterViewInit, Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import { SuccessComponent } from "../success.interface";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { Derivative } from "@models/derivative/currencies/derivative";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { SegmentService } from "@main-services/segment.service";
import { ITBStep } from "@modules/shared/components/instant-trade-bonus/entitites/itb-step";
import { ITBTradeSuccess } from "@modules/shared/components/instant-trade-bonus/entitites/itb-trade-success";
import { UntilDestroy } from "@ngneat/until-destroy";
import { LeveragedToken } from "@models/asset/cryptocoins/currencies/leveraged_tokens";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { ITaxWithheld } from "@modules/taxes/domain/entities/taxes.model";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { Helper } from "@helpers/functions";
import { LIMIT_ORDERS_SWITCH_VIEWED } from "@helpers/constants";
import { MoSuccessVisionBannerState } from "@modules/web3/components/mo-success-vision-component/mo-success-vision-banner-state.model";
import { OrderFlow } from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";
import { CheckoutEntryHelperService } from "@modules/checkout/services/checkout-entry-helper.service";
import { LimitOrdersTrackingFacade } from "@modules/limit-orders/services/orders-rudderstack.facade";
import { WEB3_FEE_BENEFFIT_FLOW, Web3VSNFeeBenefitFF } from "@modules/web3/interface/web3.benefit.ui.interface";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { RudderstackService } from "@main-services/rudderstack.service";

@UntilDestroy()
@Component({
	selector: "trade-success",
	templateUrl: "./trade-success.component.html",
	styleUrls: ["./../success-screen.component.scss"],
	standalone: false
})
export class TradeSuccessComponent extends SuccessComponent implements AfterViewInit, OnInit, OnDestroy {
	private launchDarklyService = inject(LaunchDarklyService);

	@Input()
	public itbSuccess: ITBTradeSuccess | null = null;

	@Input()
	public previousBalance: number = 0;

	@Input()
	public taxWithheld: ITaxWithheld = null;

	private cashPlusFacade = inject(CashPlusFacade);
	public isCashPlusSubscribed$: Observable<boolean>;
	public isTradeTypeSell = false;

	public visionFeeFlag$ = this.launchDarklyService
		.getFeatureConfig$<Web3VSNFeeBenefitFF>(LD_FLAG.WEB3_VSN_FEE_BENEFIT)
		.pipe(map((d) => d[WEB3_FEE_BENEFFIT_FLOW.marketOrderSell]));
	public visionId$ = this.launchDarklyService
		.getFeatureConfig$<{
			pid: string;
		}>(LD_FLAG.VISION_PID)
		.pipe(map((data) => this.tradableListService.getItemByPid(data.pid)));
	protected checkoutEntryHelper = inject(CheckoutEntryHelperService);
	protected limitOrdersTrackingFacade = inject(LimitOrdersTrackingFacade);

	protected readonly profileFacade = inject(NewProfileFacade);
	public isUkUser$ = this.profileFacade.isUkResident$();

	public ITBStep = ITBStep;
	public itbAmount: string = "";
	public showItbLoading: boolean = false;
	private loadingTimeout: ReturnType<typeof setTimeout> = null;
	private loadingDuration: number = 5000; // Ms

	protected flowName: string = "";
	protected pathPrepend: string = "";

	private _isDerivative: boolean = false;

	public leveragedTokenSuffix: string = "";

	public isLeveragedToken: boolean = false;

	public constructor(
		protected router: Router,
		protected fiatService: FiatService,
		protected accountService: AccountService,
		protected tradableListService: TradableListService,
		protected segmentService: SegmentService,
		protected rudderstackService: RudderstackService,
		protected walletListService: TradableWalletListService
	) {
		super(fiatService, tradableListService, accountService, router, segmentService, rudderstackService);
	}

	public ngOnInit(): void {
		//cash plus enabled messages
		this.cashPlusFacade.fetchApiSubscriptions();
		this.cashPlusFacade.fetchApiConfigurations();
		this.isCashPlusSubscribed$ = this.cashPlusSubscriberHelper();
		this.isTradeTypeSell = this.getTradeType() === TRADE_TYPE.SELL;

		this.isLeveragedToken =
			this.tradableListService.getItemById(this.offer.getCurrencyId()) instanceof LeveragedToken;
		if (this.isLeveragedToken) {
			this.setLeveragedTokenTypeSuffix();
		}
	}

	// Cash plus subscriber helper
	private cashPlusSubscriberHelper(): Observable<boolean> {
		return combineLatest([
			this.cashPlusFacade.isCashPlusEnabled$(),
			this.cashPlusFacade.getSubscriptionsState$().pipe(map((result) => result.subscribed)),
			this.cashPlusFacade.isFiatIdEligibleCashPlus$(this.offer.getFiatId())
		]).pipe(
			map(([cashPlusEnabled, isSubscribed, isEligible]) => {
				if (cashPlusEnabled) {
					return isSubscribed && isEligible && this.getTradeType() === TRADE_TYPE.SELL;
				}
				return false;
			})
		);
	}

	public ngAfterViewInit(): void {
		const tradeCurrencyId = this.offer.getCurrencyId();
		this._isDerivative = this.tradableListService.getItemById(tradeCurrencyId) instanceof Derivative;

		if (this.getTradeType() === TRADE_TYPE.SELL) {
			this.flowName = "Sell";
			this.pathPrepend = "/trade/sell";
		} else if (this.getTradeType() === TRADE_TYPE.BUY) {
			this.flowName = "Buy";
			this.pathPrepend = "/trade/buy";
		}
		super.ngAfterViewInit();
	}

	public isIndexTrade(): boolean {
		return this.offer.isIndexTrade();
	}

	public getTradeType(): TRADE_TYPE {
		return this.offer.getType();
	}

	public getBfcInfoText(): string {
		if (this.getTradeType() === TRADE_TYPE.BUY) {
			return "additional_gain_for_trade";
		}
		return "additional_save_from_trade";
	}

	public showBfcInfoBox(): boolean {
		return !this.isIndexTrade() && this.offer.bestFeeOptIn && !this.isDerivative();
	}

	public isDerivative(): boolean {
		return this._isDerivative;
	}

	public isToken(): boolean {
		const tradeCurrencyId = this.offer.getCurrencyId();
		return this.tradableListService.getItemById(tradeCurrencyId).isToken();
	}

	public setLeveragedTokenTypeSuffix(): void {
		const tradeType = this.offer.getType();
		if (tradeType === TRADE_TYPE.BUY) {
			this.leveragedTokenSuffix = this.previousBalance > 0 ? "increase" : "open";
		}
		if (tradeType === TRADE_TYPE.SELL) {
			this.leveragedTokenSuffix =
				+this.offer?.getAmountTradable() === this.previousBalance ? "close" : "decrease";
		}
	}

	public getTitle(): string {
		if (this.isToken()) {
			return "fma_invest";
		}
		return this.offer?.getType() + "_success";
	}

	public getVisionBannerState(): MoSuccessVisionBannerState {
		if (
			this?.offer?.getVsnFeeTokenCollection()?.globalOptIn &&
			!this.offer?.getVsnFeeTokenCollection()?.visionFeeDetail?.hasEnoughFunds
		) {
			return MoSuccessVisionBannerState.OPTEDININSUFFICIENTFUNDS;
		}
		return MoSuccessVisionBannerState.VISIONFEENOTACTIVE;
	}

	public handleBannerClick(): void {
		this.close();
		const bannerState = this.getVisionBannerState();
		if (bannerState === MoSuccessVisionBannerState.OPTEDININSUFFICIENTFUNDS) {
			this.limitOrdersTrackingFacade.updateFlow(OrderFlow.VisionUpSellBanner);
			this.visionId$.pipe(take(1)).subscribe((visionTradable) => {
				this.checkoutEntryHelper.onTradableSelect(visionTradable.getId());
			});
		} else if (bannerState === MoSuccessVisionBannerState.VISIONFEENOTACTIVE) {
			// TODO: review this
			this.router.navigateByUrl("/my-account/my-vision");
		} else {
			return;
		}
	}

	private clearTimer(): void {
		if (this.loadingTimeout) {
			clearTimeout(this.loadingTimeout);
		}
	}

	public ngOnDestroy(): void {
		this.clearTimer();
		//clear switch view cookie
		this.navigateAwayAndClearSwitchVisilibityCookie();
	}

	//cookie helper for switch visibility
	private navigateAwayAndClearSwitchVisilibityCookie(): void {
		this.launchDarklyService
			.getFeatureConfig$(LD_FLAG.LIMIT_ORDERS_SWITCH_VARIATIONS)
			.pipe(
				take(1),
				filter((config) => !!config)
			)
			.subscribe(() => {
				Helper.setCookie(LIMIT_ORDERS_SWITCH_VIEWED, null, true);
			});
	}
}
